import clsx from 'clsx'
import {
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  cloneElement,
} from 'react'
import { CardHeader, CardHeaderProps } from './CardHeader'

// Type that ensures only CardHeader components can be passed
type CardHeaderElement = ReactElement<CardHeaderProps, typeof CardHeader>

export interface Props
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  dismissible?: boolean
  flat?: boolean
  fullHeight?: boolean
  fullWidth?: boolean
  onClick?: () => void
  /**
   * Optional header - must be a CardHeader component
   */
  header?: CardHeaderElement
}

export type Ref = ForwardedRef<HTMLDivElement>

function Card(
  {
    children,
    className,
    dismissible,
    style,
    flat,
    fullHeight,
    fullWidth,
    onClick,
    header,
    ...rest
  }: Props,
  ref?: Ref,
) {
  const classes = {
    card: clsx(
      // Base card styles
      'bg-white',
      'relative',
      {
        // Conditional styles using object syntax
        'border-none': flat,
        'rounded-2.5xl shadow-spread': !flat, // 20px border radius and custom shadow
        'h-full': fullHeight,
        'w-full': fullWidth,
        // Use consistent p-6 (24px) padding, remove top padding if header is present
        'p-6': !header,
        'px-6 pb-6': header,
      },
      className,
    ),
    content: clsx('h-full', 'flex', 'flex-col'),
  }

  // Clone the header element to pass the flat prop
  const headerWithProps = header ? cloneElement(header, { flat }) : null

  return (
    <div
      className={classes.card}
      style={style}
      onClick={onClick}
      {...rest}
      ref={ref}
    >
      {headerWithProps}
      <div className={classes.content}>{children}</div>
    </div>
  )
}

export default forwardRef(Card)
